import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import StyledHeadingText from "../../components/Fonts/StyledHeadingText";
import MapComponent from "../../components/MapComponent";
import { formatDate, formatDateTime } from "../../utils/FormatDateTime";
import { patchFarmLatLng, patchSatelliteFarm, sendSatelliteReport } from "../../services/satelliteFarm";
import Button from "../../components/buttons/Button";
import { toast } from "react-toastify";
import { confirmDialog } from "../../utils/confirmDialog";
import { getUserId } from "../../helpers/local_storage_helpers";
import { FiEdit2 } from "react-icons/fi";
import SatelliteDataCharts from "./SatelliteDataCharts";

function SatelliteDetailsPop({ item, onAnyChange }) {
    const [isEditMode, setIsEditMode] = useState(false);
    const [editablePolygon, setEditablePolygon] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [originalPolygon, setOriginalPolygon] = useState([]);
    const [selectedLayer, setSelectedLayer] = useState("ndvi");

    useEffect(() => {
        if (item.farm?.geo_tag) {
            try {
                const parsedGeoTag = JSON.parse(item.farm.geo_tag);
                if (parsedGeoTag.length > 0) {
                    setEditablePolygon(parsedGeoTag);
                    setOriginalPolygon(parsedGeoTag);
                }
            } catch (error) {
                console.error("Error parsing geo_tag:", error);
            }
        }
    }, [item.farm?.geo_tag]);

    const layers = [
        { id: "ndvi", name: "NDVI (Vegetation)" },
        { id: "evi", name: "EVI (Enhanced Vegetation)" },
        { id: "savi", name: "SAVI (Soil Adjusted)" },
        { id: "ndre", name: "NDRE (Red Edge)" },
        { id: "ndmi", name: "NDMI (Moisture)" },
        { id: "ndwi", name: "NDWI (Water)" },
        { id: "soc_vis", name: "SOC VIS (Soil Organic Carbon)" },
        { id: "soc_swir", name: "SOC SWIR (Soil Organic Carbon)" },
        { id: "lai", name: "LAI (Leaf Area)" },
    ];

    const handlePolygonEdit = (newCoordinates) => {
        setEditablePolygon(newCoordinates);
    };

    const handleSave = async () => {
        if (!editablePolygon || editablePolygon.length === 0) {
            alert("No polygon data to save");
            return;
        }

        if (editablePolygon.length < 3) {
            alert("Polygon must have at least 3 points");
            return;
        }

        setIsSaving(true);
        try {
            const updateData = {
                geo_tag: JSON.stringify(editablePolygon)
            };

            const response = await patchFarmLatLng(item.farm.id, updateData);

            if (response.status === 200) {
                setIsEditMode(false);
                setOriginalPolygon([...editablePolygon]);
                toast.success("Farm updated successfully");
                onAnyChange();
            } else {
                toast.error("Unable to update farm");
            }


        } catch (error) {
            console.error("Error saving polygon:", error);
            alert("Failed to save polygon area. Please try again.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        setEditablePolygon([...originalPolygon]);
        setIsEditMode(false);
    };

    const toggleEditMode = () => {
        if (isEditMode) {
            handleCancel();
        } else {
            setIsEditMode(true);
        }
    };

    const hasChanges = () => {
        if (editablePolygon.length !== originalPolygon.length) return true;
        return editablePolygon.some((point, index) =>
            point.lat !== originalPolygon[index]?.lat ||
            point.lng !== originalPolygon[index]?.lng
        );
    };

    const handleEditNote = async () => {
        const result = await confirmDialog(
            "Update Sales Team Notes",
            "Edit Note",
            {
                showInput: true,
                inputPlaceholder: "Enter notes...",
                confirmText: "Update",
                inputValue: item.sales_team_notes || ""
            }
        );

        if (result !== false) {
            try {
                const response = await patchSatelliteFarm(item.id, {
                    sales_team_notes: result.notes,
                    sales_team: getUserId()
                });
                if (response.status === 200) {
                    toast.success("Note updated successfully");
                    onAnyChange();
                } else {
                    toast.error("Unable to update note");
                }
            } catch (error) {
                console.error("Error updating note:", error);
                toast.error("An error occurred while updating the note");
            }
        }
    };

    // Extract satellite data for selection
    const satelliteData = item.farm_satellite_data?.[0];
    const currentTileUrl = satelliteData?.[`${selectedLayer}_observation`]?.tile_url;

    const handleSendReport = async () => {
        try {
            const response = await sendSatelliteReport(item.id);
            if (response.status === 200) {
                toast.success("Report sent successfully");
            } else {
                toast.error("Unable to send report");
            }
        } catch (error) {
            toast.error("An error occurred while sending the report");
        }
    }

    return (
        <>
            {/* Header Section */}
            <div className="bg-gradient-to-r from-gray-50 to-white rounded-xl p-4 mb-6 border border-gray-100 shadow-sm">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div className="flex items-center gap-3">
                        <div className="flex flex-col">
                            <h2 className="text-xl font-bold text-gray-800">Satellite Farm Details</h2>
                            <p className="text-sm text-gray-500">{item.farm?.name || "Farm"} • {item.user_name}</p>
                        </div>
                        <button
                            onClick={handleSendReport}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary hover:bg-primary/90 text-white text-sm font-medium rounded-lg transition-colors shadow-sm"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                            </svg>
                            Send Report
                        </button>
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                        {/* Layer Selector */}
                        {item.has_satellite_data && (
                            <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2 shadow-sm">
                                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Layer</span>
                                <select
                                    value={selectedLayer}
                                    onChange={(e) => setSelectedLayer(e.target.value)}
                                    className="bg-transparent text-sm font-medium focus:outline-none cursor-pointer text-gray-700 hover:text-primary transition-colors"
                                >
                                    {layers.map(layer => (
                                        <option key={layer.id} value={layer.id}>{layer.name}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* Edit Actions */}
                        <div className="flex gap-2">
                            {!item.has_satellite_data && (
                                <Button
                                    onClick={toggleEditMode}
                                    className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${isEditMode
                                        ? 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                                        : 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm'
                                        }`}
                                >
                                    {isEditMode ? 'Cancel Edit' : 'Edit Area'}
                                </Button>
                            )}
                            {isEditMode && hasChanges() && (
                                <Button
                                    onClick={handleSave}
                                    disabled={isSaving}
                                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium text-sm disabled:opacity-50 shadow-sm transition-all"
                                >
                                    {isSaving ? 'Saving...' : 'Save Area'}
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
                <div className="w-full rounded-lg aspect-square border border-black relative overflow-hidden group">
                    {isEditMode && (
                        <div className="absolute top-2 left-2 z-[1001] bg-blue-600 text-white px-3 py-1 rounded-md text-sm font-medium shadow-lg">
                            Editing Mode
                        </div>
                    )}
                    {isEditMode && (
                        <div className="absolute top-2 right-2 z-[1001] bg-gray-800 text-white px-3 py-1 rounded-md text-sm shadow-lg">
                            Vertices: {editablePolygon.length}
                        </div>
                    )}

                    {item.has_satellite_data && currentTileUrl && (
                        <div className="absolute bottom-4 left-4 z-[1001] opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <div className="bg-white/90 backdrop-blur-sm border border-gray-200 rounded-lg px-3 py-2 shadow-xl  flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full animate-pulse bg-green-500"></div>
                                <span className="text-[10px] font-bold text-gray-700 uppercase tracking-wider">
                                    Satellite Overlay: {layers.find(l => l.id === selectedLayer)?.name}
                                </span>
                            </div>
                        </div>
                    )}

                    {
                        editablePolygon.length > 0 ?
                            <MapComponent
                                markersList={[]}
                                mapLocation={[editablePolygon[0].lat, editablePolygon[0].lng]}
                                zoomLevel={18}
                                areasList={[
                                    {
                                        title: item.farm?.name ?? "",
                                        area: editablePolygon,
                                    },
                                ]}
                                height='100%'
                                isEditMode={isEditMode}
                                onPolygonEdit={handlePolygonEdit}
                                satelliteTileUrl={currentTileUrl}
                            /> : <div className="flex items-center justify-center h-full text-gray-500">No polygon data available</div>
                    }
                </div>

                <div className="w-full">
                    {item.farm_satellite_data && (
                        <SatelliteDataCharts
                            satelliteData={item.farm_satellite_data}
                            selectedLayer={selectedLayer}
                        />
                    )}
                </div>
            </div>

            <div className="flex flex-col md:flex-row gap-4 mt-8">
                <div className="w-full flex-1">
                    <StyledHeadingText text={"Details"} size={"mt-4"} />
                    <div className='grid md:grid-cols-3 gap-4 mt-2 text-sm py-2'>
                        <div className='flex flex-col'>
                            <span className='text-gray-500'>User</span>
                            <span className='font-medium text-green-600'>{item.user_name}</span>
                        </div>
                        <div className='flex flex-col'>
                            <span className='text-gray-500'>Approval Status</span>
                            <span className='font-medium text-green-600'>{item.farm_status}</span>
                        </div>
                        <div className='flex flex-col'>
                            <span className='text-gray-500'>Farm</span>
                            <span className='font-medium text-green-600'>{item.farm?.name ?? ""}</span>
                        </div>
                        <div className='flex flex-col'>
                            <span className='text-gray-500'>Area</span>
                            <span className='font-medium text-green-600'>{item.farm?.size ?? ""} {item.farm?.area_unit ?? ""}</span>
                        </div>
                        <div className='flex flex-col'>
                            <span className='text-gray-500'>Phone</span>
                            <span className='font-medium text-green-600'>{item.user_phone_number}</span>
                        </div>
                        <div className='flex flex-col'>
                            <span className='text-gray-500'>Amount</span>
                            <span className='font-medium'>₹ {item.paid_amount}</span>
                        </div>
                        <div className='flex flex-col'>
                            <span className='text-gray-500'>Payment Status</span>
                            <span className={`${item.payment_status === "Error" ? "text-red-700" : "text-green-600"} font-bold`}>{item.payment_status}</span>
                        </div>
                        <div className='flex flex-col'>
                            <span className={new Date(item.subscription_end_date) < new Date() ? 'text-red-500' : 'text-gray-500'}>Subs. End Date</span>
                            <span className={new Date(item.subscription_end_date) < new Date() ? 'text-red-500 font-bold' : 'font-medium'}>{formatDate(item.subscription_end_date)}</span>
                        </div>
                        <div className='flex flex-col'>
                            <span className='text-gray-500'>Sub. Start Date</span>
                            <span className='font-medium'>{formatDate(item.subscription_start_date)}</span>
                        </div>
                    </div>
                    <hr className="my-2" />
                    <div className='grid md:grid-cols-3  gap-4 mt-2 text-sm py-2'>
                        <div className='flex flex-col'>
                            <span className='text-gray-500'>Irrigation Type</span>
                            <span className='font-medium text-green-600'>{item.farm?.irrigation_type ?? ""}</span>
                        </div>
                        <div className='flex flex-col'>
                            <span className='text-gray-500'>Soil Type</span>
                            <span className='font-medium text-green-600'>{item.farm?.soil_type ?? ""}</span>
                        </div>
                        <div className='flex flex-col'>
                            <span className='text-gray-500'>Source Of Irrigation</span>
                            <span className='font-medium text-green-600'>{item.farm?.source_of_irrigation ?? ""}</span>
                        </div>
                    </div>
                    <hr className="my-2" />
                    <div className='flex flex-col'>
                        <span className='text-gray-500'>Address</span>
                        <span className='font-medium text-green-600'>{item.user_address}</span>
                    </div>
                    <hr className="my-2" />
                    <div className='flex flex-col'>
                        <div className="flex items-center gap-2">
                            <span className='text-gray-500'>Sales Team Notes</span>
                            <FiEdit2
                                className="text-blue-600 cursor-pointer hover:text-blue-800"
                                onClick={handleEditNote}
                                title="Edit Note"
                            />
                        </div>
                        <span className='font-normal text-gray-700 whitespace-pre-wrap'>{item.sales_team_notes || "No notes added"}</span>
                    </div>
                    {item.farm?.image ? <StyledHeadingText text={"Image"} size={"my-2"} /> : null}
                    {item.farm?.image ? (
                        <img
                            src={item.farm?.image}
                            alt="Farm"
                            className="aspect-square h-48 object-cover rounded-lg"
                        />
                    ) : null}
                </div >
                <div className="flex-1 max-w-sm">
                    <StyledHeadingText text="Reports History" size={"my-4"} />
                    <div className="max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                        {
                            (item?.satelite_report ?? []).map((it, index) => (
                                <div key={index} className='p-4 rounded-lg border my-2'>
                                    <div className='flex flex-row gap-2 justify-between items-center'>
                                        <h3 className='font-medium text-gray-800'>{formatDateTime(it.created_at)}</h3>
                                        <Link to={it.file} target="_blank" className="bg-primary p-2 font-bold text-white rounded-full text-[0.5rem]">Click To View</Link>
                                    </div>
                                </div>
                            ))
                        }
                        {(item?.satelite_report ?? []).length === 0 && (
                            <div className="text-sm text-gray-500 italic p-2">No reports available</div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

export default SatelliteDetailsPop;